import { useEffect, useRef, useState } from 'react';
import { cnpjValido, somenteDigitos } from '../servicos/documento';
import { requisicaoAutenticada, requisicaoPublica } from '../servicos/http';

/**
 * Consulta automática de CNPJ — a Receita preenche assim que o número fecha.
 *
 * ## Sem botão "Buscar dados"
 *
 * Todo campo de CNPJ consulta sozinho quando os 14 dígitos passam no dígito
 * verificador (lista de 15/09, itens 3 e 10). O botão existia porque a
 * consulta é limitada por IP e alguém achou prudente pedir permissão; na
 * prática ele era um passo a mais que a pessoa esquecia, e o formulário ia
 * para a aprovação sem endereço. Um CNPJ válido é consultado uma vez — mudar
 * o número consulta de novo; digitar o mesmo, não.
 *
 * ## Checagem antes da Receita
 *
 * `valida-cnpj` diz se o número já está cadastrado aqui. Quem usa decide o
 * que fazer com isso (avisar, bloquear, seguir); a consulta pública só é
 * feita quando `checagem.consultarSeCadastrado` permitir — no cadastro de
 * cliente, uma empresa que já existe não precisa de dados novos.
 */

export interface ChecagemDeCnpj {
  readonly cnpj: string;
  readonly formatado: string;
  readonly valido: boolean;
  readonly jaCadastrado: boolean;
  /** RN-306 — `false` quando o cadastro existe mas está desativado. */
  readonly cadastroAtivo: boolean | null;
  readonly razaoSocialCadastrada: string | null;
}

export interface DadosDeCnpj {
  readonly cnpj: string;
  readonly razaoSocial: string;
  readonly nomeFantasia: string | null;
  readonly situacao: string | null;
  readonly dataAbertura: string | null;
  readonly naturezaJuridica: string | null;
  readonly cnaePrincipal: string | null;
  readonly cnaes: readonly { codigo: string; descricao: string; principal: boolean }[];
  /** Simples Nacional, MEI, Lucro Presumido, Lucro Real… — `null` quando a fonte não sabe. */
  readonly regimeTributario: string | null;
  readonly endereco: {
    readonly logradouro: string | null;
    readonly numero: string | null;
    readonly complemento: string | null;
    readonly bairro: string | null;
    readonly cidade: string | null;
    readonly uf: string | null;
    readonly cep: string | null;
  };
  readonly telefone: string | null;
  readonly email: string | null;
  readonly fonte: string;
}

export interface OpcoesDaConsulta {
  /** Sem sessão (calculadora, wizard público) a rota é a pública. */
  readonly publica?: boolean;
  /** `false` congela: útil enquanto o formulário reidrata um rascunho. */
  readonly ativa?: boolean;
  /** Empresa já cadastrada aqui: ainda vale ir à Receita? Padrão: sim. */
  readonly consultarSeCadastrada?: boolean;
  /** Chamado uma vez por CNPJ consultado com sucesso. */
  readonly aoCarregar: (dados: DadosDeCnpj, checagem: ChecagemDeCnpj) => void;
  /** Chamado quando a checagem responde (válido ou não, cadastrado ou não). */
  readonly aoChecar?: (checagem: ChecagemDeCnpj) => void;
}

export interface EstadoDaConsulta {
  readonly pendente: boolean;
  readonly erro: Error | null;
  readonly checagem: ChecagemDeCnpj | null;
  readonly dados: DadosDeCnpj | null;
  /** Consulta o CNPJ atual de novo — para o "tentar novamente" do aviso. */
  readonly repetir: () => void;
}

export function useConsultaDeCnpj(cnpj: string, opcoes: OpcoesDaConsulta): EstadoDaConsulta {
  const digitos = somenteDigitos(cnpj);
  const ativa = opcoes.ativa ?? true;
  const [estado, setEstado] = useState<{
    pendente: boolean;
    erro: Error | null;
    checagem: ChecagemDeCnpj | null;
    dados: DadosDeCnpj | null;
  }>({ pendente: false, erro: null, checagem: null, dados: null });
  const consultado = useRef<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  // As callbacks mudam a cada render do chamador; a consulta não deve.
  const opcoesAtuais = useRef(opcoes);
  opcoesAtuais.current = opcoes;

  useEffect(() => {
    if (!ativa) return;
    if (!cnpjValido(digitos)) {
      // Número mudou e ainda não fecha: o que foi consultado antes não vale.
      if (consultado.current !== null && consultado.current !== digitos) {
        consultado.current = null;
        setEstado({ pendente: false, erro: null, checagem: null, dados: null });
      }
      return;
    }
    if (consultado.current === digitos) return;
    consultado.current = digitos;

    let cancelada = false;
    let concluida = false;
    const requisicao =
      opcoesAtuais.current.publica === true ? requisicaoPublica : requisicaoAutenticada;
    setEstado({ pendente: true, erro: null, checagem: null, dados: null });

    void (async () => {
      try {
        const checagem = await requisicao<ChecagemDeCnpj>(`/auth/valida-cnpj?cnpj=${digitos}`);
        if (cancelada) return;
        opcoesAtuais.current.aoChecar?.(checagem);
        /*
          RN-306 — cadastro **desativado** não impede a consulta: o operador
          está justamente recadastrando a empresa, e sem os dados da Receita
          ele teria de digitar tudo à mão depois de reativar.
        */
        const cadastroAtivo = checagem.jaCadastrado && checagem.cadastroAtivo !== false;
        const pular =
          !checagem.valido ||
          (cadastroAtivo && opcoesAtuais.current.consultarSeCadastrada === false);
        if (pular) {
          setEstado({ pendente: false, erro: null, checagem, dados: null });
          return;
        }
        const dados = await requisicao<DadosDeCnpj>(`/integracoes/${digitos}`);
        if (cancelada) return;
        setEstado({ pendente: false, erro: null, checagem, dados });
        opcoesAtuais.current.aoCarregar(dados, checagem);
      } catch (erro) {
        if (cancelada) return;
        setEstado((e) => ({
          ...e,
          pendente: false,
          erro: erro instanceof Error ? erro : new Error(String(erro)),
        }));
      } finally {
        concluida = true;
      }
    })();

    return () => {
      cancelada = true;
      /**
       * Cancelada antes de terminar, a consulta não aconteceu: o CNPJ volta a
       * "não consultado" para a próxima execução refazê-la. Sem isto, o
       * monta-desmonta-remonta do StrictMode (e qualquer troca de dependência
       * no meio da requisição) deixava o campo em "Consultando…" para sempre —
       * a primeira execução marcava o CNPJ, a limpeza a cancelava e a segunda
       * o via marcado. Era o wizard chegando à etapa 2 com o CNPJ do SPED já
       * preenchido e nada acontecendo até a pessoa digitar de novo.
       */
      if (!concluida) consultado.current = null;
    };
  }, [digitos, ativa, tentativa]);

  return {
    ...estado,
    repetir: () => {
      consultado.current = null;
      setTentativa((n) => n + 1);
    },
  };
}
